import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2, Upload, Sparkles, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending_review: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-slate-100 text-slate-500 border-slate-200",
};
const CATEGORIES = ["electricity", "water", "waste", "gas", "other"];

export const BillsInboxTab = ({ canCreate }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [review, setReview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sustainability/bills");
      setBills(data.bills || []);
    } catch (e) { toast.error("Failed to load bills"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (file) => {
    if (!file) return;
    setExtracting(true);
    toast.info("AI is reading the bill — usually 10-20 seconds…");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/sustainability/bills/upload", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Extracted: ${data.supplier || "bill"} · ${data.category} · ${data.period_month || "period unknown"}`);
      setReview(data);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally { setExtracting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const act = async (action) => {
    setSaving(true);
    try {
      const body = {
        action,
        supplier: review.supplier,
        category: review.category,
        period_month: review.period_month,
        electricity_kwh: review.electricity_kwh != null ? Number(review.electricity_kwh) : null,
        water_kl: review.water_kl != null ? Number(review.water_kl) : null,
        waste_general_kg: review.waste_general_kg != null ? Number(review.waste_general_kg) : null,
        waste_recycled_kg: review.waste_recycled_kg != null ? Number(review.waste_recycled_kg) : null,
        gas_cylinders: review.gas_cylinders != null && review.gas_cylinders !== "" ? Number(review.gas_cylinders) : null,
        gas_cylinder_weight_kg: review.gas_cylinder_weight_kg != null && review.gas_cylinder_weight_kg !== "" ? Number(review.gas_cylinder_weight_kg) : null,
        gas_kg: review.gas_kg != null && review.gas_kg !== "" ? Number(review.gas_kg) : null,
        waste_collections: review.waste_collections != null && review.waste_collections !== "" ? Number(review.waste_collections) : null,
        waste_bin_size_m3: review.waste_bin_size_m3 != null && review.waste_bin_size_m3 !== "" ? Number(review.waste_bin_size_m3) : null,
        waste_density_kg_m3: review.waste_density_kg_m3 != null && review.waste_density_kg_m3 !== "" ? Number(review.waste_density_kg_m3) : null,
        waste_fullness: review.waste_fullness != null && review.waste_fullness !== "" ? Number(review.waste_fullness) : null,
        total_cost_inc_gst: review.total_cost_inc_gst != null ? Number(review.total_cost_inc_gst) : null,
      };
      await api.put(`/sustainability/bills/${review.id}`, body);
      toast.success(action === "confirm" ? "Confirmed — Sustainability totals updated (Head Office)" : action === "reject" ? "Bill rejected" : "Saved");
      setReview(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const [deletingId, setDeletingId] = useState(null);
  const onDelete = async (b) => {
    if (!window.confirm(`Delete this bill?\n\n${b.supplier || "Unknown supplier"} · ${b.period_month || "no period"} · ${b.filename || ""}\n\nIf it was confirmed, sustainability totals are recalculated.`)) return;
    setDeletingId(b.id);
    try {
      await api.delete(`/sustainability/bills/${b.id}`);
      toast.success("Bill deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeletingId(null); }
  };

  const field = (label, key, placeholder = "") => (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <Input data-testid={`bill-field-${key}`} value={review?.[key] ?? ""} placeholder={placeholder}
             onChange={(e) => setReview({ ...review, [key]: e.target.value })} className="h-8 text-sm" />
    </div>
  );

  return (
    <div data-testid="bills-inbox-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500 max-w-xl">
          Upload supplier bills (electricity, water, waste/recycling PDFs). AI extracts usage + cost,
          you review and confirm, and monthly totals land in Energy / Water / Waste under <b>Head Office</b>.
        </div>
        {canCreate && (
          <div>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden"
                   onChange={(e) => onUpload(e.target.files?.[0])} data-testid="bill-upload-input" />
            <Button size="sm" data-testid="bill-upload-btn" disabled={extracting}
                    onClick={() => fileRef.current?.click()}
                    className="bg-emerald-800 hover:bg-emerald-900 text-white">
              {extracting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              {extracting ? "Extracting…" : "Upload bill (PDF)"}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              {["Supplier", "Category", "Period", "Usage", "Cost inc GST", "Source", "Status", ""].map((c, i) => (
                <th key={i} className="text-left font-medium px-4 py-2.5">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-sm">Loading…</td></tr>
            ) : bills.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-slate-500 text-sm">
                No bills yet — upload your first PDF and AI will do the data entry.
              </td></tr>
            ) : bills.map((b) => (
              <tr key={b.id} data-testid={`bill-row-${b.id}`} className="border-b border-slate-100">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-slate-400" />{b.supplier || "—"}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate max-w-[220px]">{b.filename}</div>
                </td>
                <td className="px-4 py-3 capitalize text-slate-600">{b.category}</td>
                <td className="px-4 py-3 tabular-nums text-slate-600">{b.period_month || "—"}</td>
                <td className="px-4 py-3 text-[12px] text-slate-600">
                  {b.electricity_kwh != null && `${Number(b.electricity_kwh).toLocaleString()} kWh`}
                  {b.water_kl != null && `${Number(b.water_kl).toLocaleString()} kL`}
                  {b.waste_general_kg != null && `${Number(b.waste_general_kg).toLocaleString()} kg general `}
                  {b.waste_recycled_kg != null && `${Number(b.waste_recycled_kg).toLocaleString()} kg recycled${b.waste_estimated ? " (est.)" : ""}`}
                  {b.gas_kg != null && `${Number(b.gas_kg).toLocaleString()} kg LPG`}
                  {b.electricity_kwh == null && b.water_kl == null && b.waste_general_kg == null && b.waste_recycled_kg == null && b.gas_kg == null && "—"}
                </td>
                <td className="px-4 py-3 tabular-nums text-slate-700">
                  {b.total_cost_inc_gst != null ? `$${Number(b.total_cost_inc_gst).toLocaleString()}` : "—"}
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-500">{b.source === "power_automate_email" ? "Email" : "Upload"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_BADGE[b.status] || STATUS_BADGE.rejected}`}>
                    {b.status === "pending_review" ? "Needs review" : b.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" data-testid={`bill-review-${b.id}`}
                            onClick={() => setReview(b)} className="h-7 text-xs">
                      {b.status === "pending_review" ? "Review" : "View"}
                    </Button>
                    {canCreate && (
                      <Button size="sm" variant="ghost" data-testid={`bill-delete-${b.id}`}
                              disabled={deletingId === b.id} onClick={() => onDelete(b)}
                              className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                        {deletingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-lg" data-testid="bill-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-emerald-700" /> Review extracted bill
            </DialogTitle>
          </DialogHeader>
          {review && (
            <div className="space-y-3">
              <div className="text-[11px] text-slate-500">
                {review.filename} · AI confidence: <b>{review.extracted?.confidence || "—"}</b>
                {review.extracted?.line_summary && <div className="mt-1 italic">"{review.extracted.line_summary}"</div>}
              </div>
              {review.extracted?.period_start && review.extracted?.period_end &&
               review.extracted.period_start.slice(0, 7) !== review.extracted.period_end.slice(0, 7) && (
                <div data-testid="bill-multi-month-note"
                     className="text-[11px] rounded-md bg-sky-50 border border-sky-200 text-sky-800 px-3 py-2">
                  Billing period <b>{review.extracted.period_start} → {review.extracted.period_end}</b> spans
                  multiple months — on confirm, usage and cost are automatically split pro-rata (by days)
                  across each month covered.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {field("Supplier", "supplier")}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
                  <select data-testid="bill-field-category" value={review.category || "other"}
                          onChange={(e) => setReview({ ...review, category: e.target.value })}
                          className="w-full h-8 text-sm border border-slate-200 rounded-md px-2 bg-white">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {field("Period month (YYYY-MM)", "period_month", "2026-07")}
                {field("Cost inc GST ($)", "total_cost_inc_gst")}
                {review.category === "electricity" && field("Electricity (kWh)", "electricity_kwh")}
                {review.category === "water" && field("Water (kL)", "water_kl")}
                {review.category === "waste" && field("General waste (kg)", "waste_general_kg")}
                {review.category === "waste" && field("Recycled (kg)", "waste_recycled_kg")}
                {review.category === "waste" && field("Collections (bin lifts)", "waste_collections")}
                {review.category === "waste" && field("Bin size (m³)", "waste_bin_size_m3", "1.5")}
                {review.category === "waste" && field("Avg fullness (0–1)", "waste_fullness", "1.0")}
                {review.category === "waste" && field("Density (kg/m³)", "waste_density_kg_m3", "55 loose cardboard")}
                {review.category === "gas" && field("Cylinders exchanged", "gas_cylinders")}
                {review.category === "gas" && field("Gas per cylinder (kg)", "gas_cylinder_weight_kg")}
                {review.category === "gas" && field("Total gas (kg)", "gas_kg", "cylinders × kg each")}
              </div>
              {review.category === "waste" && (() => {
                const cols = Number(review.waste_collections) || 0;
                const size = Number(review.waste_bin_size_m3) || 0;
                const full = Number(review.waste_fullness) || 1;
                const dens = Number(review.waste_density_kg_m3) || 55;
                if (!cols || !size) return null;
                const est = cols * size * full * dens;
                return (
                  <div data-testid="bill-recycling-estimate-note"
                       className="text-[11px] rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-3 py-2">
                    <b>{cols} collection(s) × {size} m³ × {full} fullness × {dens} kg/m³ =
                    {" "}{est.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg estimated recycling.</b>
                    <button type="button" data-testid="bill-apply-recycling-estimate"
                            className="ml-2 underline font-medium"
                            onClick={() => setReview({ ...review, waste_recycled_kg: Math.round(est * 10) / 10 })}>
                      Apply to Recycled (kg)
                    </button>
                    <div className="mt-1">Bin rental &amp; surcharges are not collections. Emissions stay
                    <b> pending a verified factor</b> — ask Veolia for monthly weights, recycling destination
                    and their emissions factor. Recycling savings are reported separately from GBE's totals.</div>
                  </div>
                );
              })()}
              {review.category === "gas" && (() => {
                const kg = Number(review.gas_kg) || (Number(review.gas_cylinders) || 0) * (Number(review.gas_cylinder_weight_kg) || 0);
                if (!kg) return null;
                const s1 = kg * 3.05, s3 = kg * 1.02;
                return (
                  <div data-testid="bill-gas-emissions-note"
                       className="text-[11px] rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2">
                    <b>{kg.toLocaleString()} kg LPG</b> → Scope 1 (forklift combustion): <b>{s1.toFixed(0)} kg CO₂-e</b> (×3.05)
                    + Scope 3 upstream: <b>{s3.toFixed(0)} kg CO₂-e</b> (×1.02)
                    = <b>{(s1 + s3).toFixed(0)} kg CO₂-e</b> combined. Applied on confirm.
                  </div>
                );
              })()}
              <div className="flex items-center justify-between pt-2">
                <Button size="sm" variant="outline" data-testid="bill-reject-btn" disabled={saving}
                        onClick={() => act("reject")} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
                </Button>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="bill-save-btn" disabled={saving}
                          onClick={() => act("save")}>Save draft</Button>
                  <Button size="sm" data-testid="bill-confirm-btn" disabled={saving}
                          onClick={() => act("confirm")}
                          className="bg-emerald-800 hover:bg-emerald-900 text-white">
                    {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                    Confirm → Sustainability
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
